import type { TypedDocumentNode } from '@apollo/client'
import type * as GraphQLOperations from 'types/graphql'

import { useState } from 'react'
import { toast } from 'react-hot-toast'

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'

import PortalModeSwitcher, {
  type PortalMode,
} from 'src/components/businessPortal/PortalModeSwitcher'
import {
  Alert,
  Button,
  ConfirmationDialog,
  DialogActions,
  DialogBody,
  DialogHeader,
  DialogTitle,
  Divider,
  HistoryIcon,
  Icon,
  IconButton,
  MessageSquareIcon,
  MessagesCircleIcon,
  PlusIcon,
  Popover,
  Spinner,
  Text,
  Tooltip,
  Trash2Icon,
} from 'src/design-system/ui'

import { ANALYTICS_CONVERSATIONS } from './conversationData'
import './conversations-sidebar.css'

const DELETE_ANALYTICS_CONVERSATION: TypedDocumentNode<
  GraphQLOperations.DeleteAnalyticsConversationMutation,
  GraphQLOperations.DeleteAnalyticsConversationMutationVariables
> = gql`
  mutation DeleteAnalyticsConversationMutation($id: String!) {
    deleteAnalyticsConversation(id: $id) {
      success
    }
  }
`

const COLLAPSED_RECENT_CONVERSATION_LIMIT = 10

export interface ConversationRow {
  id: string
  title?: string | null
  pm?: string | null
  updatedAt: string
}

const AgentSidebar = ({
  activitySelected,
  expanded,
  onModeChange,
  onNewChat,
  onOpenActivity,
  onSelectConversation,
  pm,
  selectedId,
  workspaceControls,
}: {
  activitySelected: boolean
  expanded: boolean
  onModeChange: (mode: PortalMode) => void
  onNewChat: () => void
  onOpenActivity: () => void
  onSelectConversation: (id: string) => void
  pm?: string | null
  selectedId?: string | null
  workspaceControls?: React.ReactNode
}) => {
  const { data, error, loading, refetch } = useQuery(ANALYTICS_CONVERSATIONS, {
    variables: { pm: pm ?? null },
    fetchPolicy: 'cache-and-network',
  })
  const conversations: ConversationRow[] = data?.analyticsConversations ?? []
  const [deleteTarget, setDeleteTarget] = useState<ConversationRow | null>(null)
  const [deleteConversation, { loading: deleting }] = useMutation(
    DELETE_ANALYTICS_CONVERSATION,
    { onCompleted: () => void refetch() }
  )

  const handleDeleteConversation = () => {
    if (deleting || !deleteTarget) return
    const { id } = deleteTarget
    void deleteConversation({ variables: { id } })
      .then((result) => {
        if (!result.data?.deleteAnalyticsConversation?.success) {
          toast.error('Unable to delete conversation.')
        } else {
          setDeleteTarget(null)
          if (id === selectedId) onNewChat()
        }
      })
      .catch(() => toast.error('Unable to delete conversation.'))
  }

  if (!expanded) {
    return (
      <CollapsedAgentSidebar
        conversations={conversations.slice(
          0,
          COLLAPSED_RECENT_CONVERSATION_LIMIT
        )}
        error={Boolean(error)}
        loading={loading}
        onModeChange={onModeChange}
        onNewChat={onNewChat}
        onSelectConversation={onSelectConversation}
        selectedId={selectedId}
      />
    )
  }

  return (
    <div className="agent-sidebar">
      <div className="agent-sidebar-header">
        <PortalModeSwitcher expanded mode="agent" onChange={onModeChange} />
        <Button
          variant="quiet"
          className="agent-sidebar-action"
          onPress={onNewChat}
          leadingIcon={<Icon icon={PlusIcon} size="sm" tone="current" />}
        >
          New chat
        </Button>
      </div>
      <div className="agent-sidebar-history">
        {error ? (
          <Alert tone="error">Recent chats couldn&apos;t be loaded.</Alert>
        ) : loading && conversations.length === 0 ? (
          <div className="agent-sidebar-empty">
            <Spinner aria-label="Loading conversations" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="agent-sidebar-empty">
            <Icon icon={MessageSquareIcon} size="lg" tone="muted" />
            <Text variant="caption" tone="muted">
              No conversations yet. Ask something to start one.
            </Text>
          </div>
        ) : (
          <ul className="agent-sidebar-list">
            {conversations.map((conversation) => {
              const selected =
                !activitySelected && conversation.id === selectedId
              return (
                <li
                  className="agent-sidebar-conversation"
                  data-current={selected || undefined}
                  key={conversation.id}
                >
                  <Button
                    variant="quiet"
                    className="agent-sidebar-conversation-open"
                    aria-current={selected ? 'page' : undefined}
                    onPress={() => onSelectConversation(conversation.id)}
                  >
                    <Text
                      variant="caption"
                      className="agent-sidebar-conversation-title"
                    >
                      {conversation.title ?? 'New conversation'}
                    </Text>
                  </Button>
                  <Tooltip content="Delete conversation">
                    <IconButton
                      className="agent-sidebar-delete"
                      aria-label={`Delete ${conversation.title ?? 'conversation'}`}
                      isDisabled={deleting}
                      onPress={() => setDeleteTarget(conversation)}
                    >
                      <Icon icon={Trash2Icon} size="xs" tone="current" />
                    </IconButton>
                  </Tooltip>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null)
        }}
        width="compact"
        isKeyboardDismissDisabled={deleting}
      >
        <DialogHeader>
          <DialogTitle>Delete conversation?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>
            This permanently deletes{' '}
            {deleteTarget?.title ?? 'this conversation'}. This cannot be undone.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button
            variant="quiet"
            isDisabled={deleting}
            onPress={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button
            tone="error"
            isDisabled={deleting}
            isLoading={deleting}
            onPress={handleDeleteConversation}
          >
            Delete conversation
          </Button>
        </DialogActions>
      </ConfirmationDialog>
      <div className="agent-sidebar-footer">
        <Button
          variant="quiet"
          className="agent-sidebar-action"
          aria-current={activitySelected ? 'page' : undefined}
          onPress={onOpenActivity}
          leadingIcon={<Icon icon={HistoryIcon} size="sm" tone="current" />}
        >
          Activity
        </Button>
        {workspaceControls && (
          <>
            <Divider spacing="none" />
            {workspaceControls}
          </>
        )}
      </div>
    </div>
  )
}

export const CollapsedAgentSidebar = ({
  conversations,
  error,
  loading,
  onModeChange,
  onNewChat,
  onSelectConversation,
  selectedId,
}: {
  conversations: ConversationRow[]
  error: boolean
  loading: boolean
  onModeChange: (mode: PortalMode) => void
  onNewChat: () => void
  onSelectConversation: (id: string) => void
  selectedId?: string | null
}) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  return (
    <div className="agent-sidebar-collapsed">
      <PortalModeSwitcher
        expanded={false}
        mode="agent"
        onChange={(mode) => {
          setHistoryOpen(false)
          onModeChange(mode)
        }}
      />
      <Tooltip content="New chat" placement="right">
        <IconButton
          aria-label="New chat"
          onPress={() => {
            setHistoryOpen(false)
            onNewChat()
          }}
        >
          <Icon icon={PlusIcon} size="sm" />
        </IconButton>
      </Tooltip>
      <Popover
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        ariaLabel="Recent chats"
        placement="right top"
        className="agent-sidebar-recent-popover"
        trigger={
          <IconButton aria-label="Recent chats">
            <Icon icon={MessagesCircleIcon} size="sm" />
          </IconButton>
        }
      >
        <Text variant="caption-strong">Recent chats</Text>
        <Divider spacing="none" />
        {loading ? (
          <Spinner aria-label="Loading conversations" />
        ) : error ? (
          <Text variant="caption" tone="muted">
            Recent chats couldn&apos;t be loaded.
          </Text>
        ) : conversations.length === 0 ? (
          <Text variant="caption" tone="muted">
            No recent chats yet.
          </Text>
        ) : (
          <div className="agent-sidebar-recent-list">
            {conversations.map((conversation) => (
              <Button
                key={conversation.id}
                variant="quiet"
                className="agent-sidebar-recent-action"
                aria-current={
                  conversation.id === selectedId ? 'page' : undefined
                }
                onPress={() => {
                  setHistoryOpen(false)
                  onSelectConversation(conversation.id)
                }}
              >
                {conversation.title ?? 'New conversation'}
              </Button>
            ))}
          </div>
        )}
      </Popover>
    </div>
  )
}

export default AgentSidebar
